import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Slide,
  Divider,
  Chip,
} from "@mui/material";
import { CheckCircle, RadioButtonUnchecked, Add, LocationOn } from "@mui/icons-material";

// IMPORTA TUS SERVICIOS ORIGINALES
import { getProvincias } from "../../services/ProvinciaServices";
import { getDepartamentos } from "../../services/DepartamentosServices";
import { getLocalidades } from "../../services/LocalidadesServices";
import { getBarrios } from "../../services/BarriosServices";

// ─── Estilos reutilizables ───────────────────────────────────────────────────
const cardBase = {
  border: "1.5px solid",
  borderRadius: "12px",
  padding: "14px 18px",
  cursor: "pointer",
  transition: "all 0.15s ease",
  mb: 1.5,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
};

const cardSelected = {
  borderColor: "primary.main",
  bgcolor: "primary.50",
};

const cardUnselected = {
  borderColor: "divider",
  bgcolor: "background.paper",
  "&:hover": { borderColor: "primary.light", bgcolor: "grey.50" },
};

// ─── Subcomponente: opción de dirección ──────────────────────────────────────
const DireccionOption = ({ value, selected, label, sublabel, badge, onClick }) => (
  <Box sx={{ ...cardBase, ...(selected ? cardSelected : cardUnselected) }} onClick={onClick}>
    {selected ? (
      <CheckCircle color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
    ) : (
      <RadioButtonUnchecked fontSize="small" sx={{ color: "text.disabled", flexShrink: 0 }} />
    )}
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" fontWeight={600} color={selected ? "primary.dark" : "text.secondary"} sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.primary" mt={0.25}>
        {sublabel}
      </Typography>
    </Box>
    {badge && (
      <Chip label={badge} size="small" color="primary" sx={{ fontWeight: 600, fontSize: "11px" }} />
    )}
  </Box>
);

// ─── Modal principal ─────────────────────────────────────────────────────────
const ModalDireccionEntrega = ({
  open,
  onClose,
  direccionCliente,
  direccionGuardada,
  onGuardarDireccion,
  onReemplazarDireccion,
  onUsarDireccionPedido,
}) => {
  const [modo, setModo] = useState("nueva");
  const [confirmarGuardar, setConfirmarGuardar] = useState(false);
  const [confirmarReemplazar, setConfirmarReemplazar] = useState(false);
  const [confirmarReemplazarDoble, setConfirmarReemplazarDoble] = useState(false);

  const [form, setForm] = useState({
    calle: "", nro: "", piso: "", dpto: "", cp: "",
    provincia: "", departamento: "", localidad: "", barrio: "", observacion: "",
  });
  const [errors, setErrors] = useState({});
  const [provincias, setProvincias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [barrios, setBarrios] = useState([]);

  // ── Carga de datos geográficos ──
  useEffect(() => {
    getProvincias().then(r => setProvincias(r || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (!form.provincia) return setDepartamentos([]);
    getDepartamentos(form.provincia).then(r => setDepartamentos(r || [])).catch(console.error);
  }, [form.provincia]);

  useEffect(() => {
    if (!form.departamento) return setLocalidades([]);
    getLocalidades(form.departamento).then(r => setLocalidades(r || [])).catch(console.error);
  }, [form.departamento]);

  useEffect(() => {
    if (!form.localidad) return setBarrios([]);
    getBarrios(form.localidad).then(r => setBarrios(r || [])).catch(console.error);
  }, [form.localidad]);

  // ── Modo inicial ──
  useEffect(() => {
    if (direccionCliente) setModo("perfil");
    else if (direccionGuardada) setModo("guardada");
    else setModo("nueva");
  }, [direccionCliente, direccionGuardada]);

  // ── Validación ──
  const validar = () => {
    const e = {};
    if (!form.calle.trim()) e.calle = "La calle es obligatoria";
    if (!form.nro.trim()) e.nro = "El número es obligatorio";
    if (!form.provincia) e.provincia = "Seleccione una provincia";
    if (!form.departamento) e.departamento = "Seleccione un departamento";
    if (!form.localidad) e.localidad = "Seleccione una localidad";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildDireccionConNombre = () => {
    const provinciaSeleccionada = provincias.find(p => p.id === form.provincia);
    const departamentoSeleccionado = departamentos.find(d => d.id === form.departamento);
    const localidadSeleccionada = localidades.find(l => l.id === form.localidad);
    return {
      ...form,
      provinciaNombre: provinciaSeleccionada?.label || "",
      departamentoNombre: departamentoSeleccionado?.label || "",
      localidadNombre: localidadSeleccionada?.label || "",
    };
  };

  // ── Confirmar dirección ──
  const handleConfirmar = () => {
    if (modo === "perfil") { onUsarDireccionPedido(direccionCliente); onClose(); return; }
    if (modo === "guardada") { onUsarDireccionPedido(direccionGuardada); onClose(); return; }
    if (!validar()) return;

    if (!direccionCliente && !direccionGuardada) { setConfirmarGuardar(true); return; }
    if (direccionCliente && direccionGuardada) { setConfirmarReemplazarDoble(true); return; }
    setConfirmarReemplazar(true);
  };

  // ── Acciones de confirmación ──
  const guardarEnPerfil = () => {
    const d = buildDireccionConNombre();
    onGuardarDireccion(d);
    onUsarDireccionPedido(d);
    setConfirmarGuardar(false);
    onClose();
  };

  const reemplazarUnica = () => {
    const d = buildDireccionConNombre();
    onReemplazarDireccion(d);
    onUsarDireccionPedido(d);
    setConfirmarReemplazar(false);
    onClose();
  };

  const reemplazarPerfil = () => {
    const d = buildDireccionConNombre();
    onReemplazarDireccion({ ...d, destino: "perfil" });
    onUsarDireccionPedido(d);
    setConfirmarReemplazarDoble(false);
    onClose();
  };

  const reemplazarAlternativa = () => {
    const d = buildDireccionConNombre();
    onReemplazarDireccion({ ...d, destino: "alternativa" });
    onUsarDireccionPedido(d);
    setConfirmarReemplazarDoble(false);
    onClose();
  };

  const usarSinGuardar = () => {
    onUsarDireccionPedido(form);
    setConfirmarGuardar(false);
    setConfirmarReemplazar(false);
    setConfirmarReemplazarDoble(false);
    onClose();
  };

  const field = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const resetDep = (key) => {
    const resets = {
      provincia: { departamento: "", localidad: "", barrio: "" },
      departamento: { localidad: "", barrio: "" },
      localidad: { barrio: "" },
    };
    setForm(f => ({ ...f, [key]: undefined, ...resets[key] }));
  };

  return (
    <>
      {/* ── MODAL PRINCIPAL ── */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.2)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Paso 2 de 3
          </Typography>
          <DialogTitle sx={{ p: 0, mt: 0.25, fontSize: "18px", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOn color="primary" fontSize="small" />
            Dirección de envío
          </DialogTitle>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 0 }}>
          {/* Opciones guardadas */}
          {direccionCliente && (
            <DireccionOption
              value="perfil"
              selected={modo === "perfil"}
              label="Dirección del perfil"
              sublabel={`${direccionCliente.calle} ${direccionCliente.nro}`}
              badge="Predeterminada"
              onClick={() => setModo("perfil")}
            />
          )}

          {direccionGuardada && (
            <DireccionOption
              value="guardada"
              selected={modo === "guardada"}
              label="Dirección alternativa"
              sublabel={`${direccionGuardada.calle} ${direccionGuardada.nro}`}
              onClick={() => setModo("guardada")}
            />
          )}

          {/* Opción nueva dirección */}
          <Box
            sx={{
              ...cardBase,
              ...(modo === "nueva" ? cardSelected : cardUnselected),
            }}
            onClick={() => setModo("nueva")}
          >
            {modo === "nueva" ? (
              <CheckCircle color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
            ) : (
              <Add fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />
            )}
            <Typography variant="body2" fontWeight={600} color={modo === "nueva" ? "primary.dark" : "text.primary"}>
              Ingresar nueva dirección
            </Typography>
          </Box>

          {/* Formulario */}
          {modo === "nueva" && (
            <Box sx={{ mt: 2, pb: 2 }}>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Nueva dirección
                </Typography>
              </Divider>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={7}>
                  <TextField
                    label="Calle" fullWidth required size="small"
                    value={form.calle} error={!!errors.calle} helperText={errors.calle}
                    onChange={e => field("calle", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <TextField
                    label="Número" fullWidth required size="small"
                    value={form.nro} error={!!errors.nro} helperText={errors.nro}
                    onChange={e => field("nro", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <TextField
                    label="Cód. postal" fullWidth size="small"
                    value={form.cp} onChange={e => field("cp", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Piso" fullWidth size="small"
                    value={form.piso} onChange={e => field("piso", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Depto" fullWidth size="small"
                    value={form.dpto} onChange={e => field("dpto", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} />

                <Grid item xs={12} sm={6}>
                  <TextField
                    select label="Provincia" fullWidth required size="small"
                    value={form.provincia} error={!!errors.provincia} helperText={errors.provincia}
                    onChange={e => { field("provincia", e.target.value); resetDep("provincia"); }}
                  >
                    {provincias.map(p => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select label="Departamento" fullWidth required size="small"
                    value={form.departamento} error={!!errors.departamento} helperText={errors.departamento}
                    disabled={!form.provincia}
                    onChange={e => { field("departamento", e.target.value); resetDep("departamento"); }}
                  >
                    {departamentos.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select label="Localidad" fullWidth required size="small"
                    value={form.localidad} error={!!errors.localidad} helperText={errors.localidad}
                    disabled={!form.departamento}
                    onChange={e => { field("localidad", e.target.value); resetDep("localidad"); }}
                  >
                    {localidades.map(l => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select label="Barrio" fullWidth size="small"
                    value={form.barrio} disabled={!form.localidad}
                    onChange={e => field("barrio", e.target.value)}
                  >
                    {barrios.map(b => <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>)}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Observaciones" fullWidth multiline rows={2} size="small"
                    placeholder="Ej: timbre B, portón azul, referencias..."
                    value={form.observacion} onChange={e => field("observacion", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", gap: 1 }}>
          <Button onClick={onClose} variant="outlined" color="inherit" sx={{ borderRadius: "8px", textTransform: "none" }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleConfirmar} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3 }}>
            Confirmar dirección →
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MODAL: GUARDAR ── */}
      <ConfirmSheet
        open={confirmarGuardar}
        onClose={() => setConfirmarGuardar(false)}
        title="¿Guardar esta dirección?"
        description="Podés guardarla en tu perfil para usarla en próximos pedidos."
        actions={[
          { label: "Volver", onClick: () => setConfirmarGuardar(false), variant: "outlined" },
          { label: "Usar sin guardar", onClick: usarSinGuardar, variant: "outlined" },
          { label: "Guardar en perfil", onClick: guardarEnPerfil, variant: "contained" },
        ]}
      />

      {/* ── MODAL: REEMPLAZAR ÚNICA ── */}
      <ConfirmSheet
        open={confirmarReemplazar}
        onClose={() => setConfirmarReemplazar(false)}
        title="¿Reemplazar dirección guardada?"
        description="Ya tenés una dirección guardada. ¿Querés reemplazarla con la nueva?"
        actions={[
          { label: "Volver", onClick: () => setConfirmarReemplazar(false), variant: "outlined" },
          { label: "Usar sin guardar", onClick: usarSinGuardar, variant: "outlined" },
          { label: "Reemplazar", onClick: reemplazarUnica, variant: "contained" },
        ]}
      />

      {/* ── MODAL: REEMPLAZAR DOBLE ── */}
      <ConfirmSheet
        open={confirmarReemplazarDoble}
        onClose={() => setConfirmarReemplazarDoble(false)}
        title="¿Qué dirección reemplazamos?"
        description="Tenés una dirección en el perfil y una alternativa. Elegí cuál reemplazar."
        actions={[
          { label: "Volver", onClick: () => setConfirmarReemplazarDoble(false), variant: "outlined" },
          { label: "Usar sin guardar", onClick: usarSinGuardar, variant: "outlined" },
          { label: "Reemplazar perfil", onClick: reemplazarPerfil, variant: "outlined", color: "primary" },
          { label: "Reemplazar alternativa", onClick: reemplazarAlternativa, variant: "contained" },
        ]}
      />
    </>
  );
};

// ─── Subcomponente: sheet de confirmación ────────────────────────────────────
const ConfirmSheet = ({ open, onClose, title, description, actions }) => (
  <Dialog
    open={open}
    onClose={onClose}
    TransitionComponent={Slide}
    TransitionProps={{ direction: "up" }}
    PaperProps={{
      sx: {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        m: 0,
        borderRadius: "20px 20px 0 0",
        maxWidth: "100%",
        width: "100%",
        px: 3,
        py: 2.5,
      },
    }}
  >
    <Box sx={{ width: 40, height: 4, bgcolor: "divider", borderRadius: 2, mx: "auto", mb: 2 }} />
    <Typography variant="subtitle1" fontWeight={600} mb={0.75}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" mb={2.5}>{description}</Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-end" }}>
      {actions.map((a, i) => (
        <Button
          key={i}
          variant={a.variant}
          color={a.color || (a.variant === "contained" ? "primary" : "inherit")}
          onClick={a.onClick}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 500 }}
        >
          {a.label}
        </Button>
      ))}
    </Box>
  </Dialog>
);

export default ModalDireccionEntrega;